import React, { useState, useRef } from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { DatePicker } from '../ui/DatePicker';
import { ExtraPaymentsWarningModal } from '../ExtraPaymentsWarningModal';
import type { ExtraPaymentsData } from '../../store/calculatorStore';

interface ExtraPaymentsSectionProps {
  tabId: string;
}

export const ExtraPaymentsSection: React.FC<ExtraPaymentsSectionProps> = ({ tabId }) => {
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const updateCalculatorTabData = useCalculatorStore(s => s.updateCalculatorTabData);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    field: keyof ExtraPaymentsData;
    value: ExtraPaymentsData[keyof ExtraPaymentsData];
  } | null>(null);
  const activeFieldRef = useRef<HTMLInputElement | null>(null);
  
  const tab = calculatorTabs.find(t => t.id === tabId);
  
  if (!tab) {
    return <div className="p-4 text-center text-ink-muted">Calculator not found</div>;
  }
  
  const { extraPayments, hasManualExtraPaymentEdits } = tab.data;
  
  const handleChange = (
    field: keyof ExtraPaymentsData,
    value: ExtraPaymentsData[keyof ExtraPaymentsData],
    element?: HTMLInputElement
  ) => {
    // Store the active element for focus management
    if (element) {
      activeFieldRef.current = element;
    }

    // Only show warning if there are actual manual edits in the schedule
    const hasActualManualEdits = tab.data.amortizationSchedule.some(entry => entry.oneTimePayment !== undefined);
    
    if (hasManualExtraPaymentEdits && hasActualManualEdits) {
      setPendingChanges({ field, value });
      setIsWarningModalOpen(true);
    } else {
      applyChanges(field, value);
    }
  };

  const applyChanges = (
    field: keyof ExtraPaymentsData,
    value: ExtraPaymentsData[keyof ExtraPaymentsData]
  ) => {
    const nextExtraPayments = {
      ...extraPayments,
      [field]: value
    };
    if (nextExtraPayments.amount > 0 && !nextExtraPayments.startDate) {
      nextExtraPayments.startDate = tab.data.mortgageTerms.startDate;
    }

    // First update the extra payments data and clear manual edits flag
    updateCalculatorTabData(tabId, {
      extraPayments: nextExtraPayments,
      hasManualExtraPaymentEdits: false,  // Explicitly set to false
      amortizationSchedule: tab.data.amortizationSchedule.map(entry => ({
        ...entry,
        oneTimePayment: undefined,  // Clear any manual payments
        totalPayment: entry.payment + entry.expensePayment  // Recalculate total without manual payments
      }))
    });

    // Return focus to the active field after a short delay to allow for state updates
    setTimeout(() => {
      if (activeFieldRef.current) {
        activeFieldRef.current.focus();
      }
    }, 0);
  };
  
  return (
    <div className="px-5 pb-5 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Input
            label="Extra Payment Amount"
            type="number"
            value={extraPayments.amount}
            onChange={(e) => handleChange('amount', Number(e.target.value), e.target)}
            prefix="$"
            formatWithCommas={true}
          />
        </div>
        
        <div>
          <Select
            label="Payment Frequency"
            value={extraPayments.frequency}
            onChange={(value) => handleChange('frequency', value)}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'semi-monthly', label: 'Semi-monthly (1st & 15th)' },
              { value: 'bi-weekly', label: 'Bi-weekly' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'yearly', label: 'Yearly' }
            ]}
          />
        </div>
        
        <div>
          <DatePicker
            label="Start Date"
            selectedDate={extraPayments.startDate || null}
            onChange={(date) => handleChange('startDate', date)}
            showMonthYearPicker={true}
            placeholderText="Month and year"
          />
        </div>

        <div>
          <DatePicker
            label="End Date"
            selectedDate={extraPayments.endDate || null}
            onChange={(date) => handleChange('endDate', date)}
            showMonthYearPicker={true}
            placeholderText="Month and year"
          />
        </div>
      </div>
      
      <p className="text-xs text-ink-muted">To add one-time payments, edit the Amortization Schedule table below.</p>

      <ExtraPaymentsWarningModal
        isOpen={isWarningModalOpen}
        onClose={() => {
          setIsWarningModalOpen(false);
          setPendingChanges(null);
          // Return focus to the active field when closing without proceeding
          if (activeFieldRef.current) {
            activeFieldRef.current.focus();
          }
        }}
        onConfirm={() => {
          if (pendingChanges) {
            applyChanges(pendingChanges.field, pendingChanges.value);
          }
          setIsWarningModalOpen(false);
          setPendingChanges(null);
        }}
      />
    </div>
  );
};